import { useReducer, useState, useCallback, useMemo } from 'react'; // Import useMemo
import { createContext } from 'use-context-selector';
import type { FC } from 'react';
import orderApi from '../api/OrderApi';
import { initialOrder, OrderEntry, OrderItem, SFAOrderNote } from 'src/types/orderEntry';
import { AddItemRequest } from 'src/types/addItemRequest';
import { ContextProviderProps } from 'src/types/contextProviderProps';
import { FieldErrors } from 'src/types/orderHeaderValidationErrors';
import { Warehouse } from 'src/types/Warehouse';
import { getGreatestLeadTimeOfUnavailableItems } from 'src/utils/availabilityHelper';
import moment from 'moment';

export interface OrderContextValue {
  order: OrderEntry;
  saveOrder: (order: OrderEntry) => void;
  saveOrderHeader: () => Promise<boolean>;
  addItem: (request: AddItemRequest) => void;
  updateItem: (sfaOrderItemId: number, quantity: number, programId?: number) => void;
  deleteItem: (sfaOrderItemId: number, programId?: number) => void;
  selectItem: (sfaOrderItemId: number) => void;
  loadOrder: (sfaOrderId: number, selectedOrderItemId?: number, defaultShipToNumber?: string, defaultWarehouseCode?: string) => Promise<OrderEntry | null>;
  warehousesFiltered?: Warehouse[];
  setWarehousesFiltered?: React.Dispatch<React.SetStateAction<Warehouse[]>>;
  startNewOrder: (customerNumber: number, orderType: number, termCode: string, programId?: number) => Promise<number>;
  saveNotes: (notes: SFAOrderNote[]) => Promise<void>;
  orderHeaderErrors?: FieldErrors;
  setOrderHeaderErrors: React.Dispatch<React.SetStateAction<FieldErrors>>;
  validateOrderHeader: (poRequired: boolean) => boolean;
  updateOrderField: (field: keyof OrderEntry, value: any) => void;
  greatestLeadTime: number | null;
  itemNumberWithGreatestLeadTime: string | null;
}

type SaveOrderAction = { type: 'SaveOrder'; payload: { order: OrderEntry } };
type SaveOrderHeaderAction = { type: 'SaveOrderHeader'; payload: { orderHeader: OrderEntry } };
type LoadOrderAction = { type: 'LoadOrder'; payload: { order: OrderEntry } };
type LoadOrderAndSelectAction = { type: 'LoadOrderAndSelect'; payload: { order: OrderEntry; selectedOrderItemId: number } };
type StartNewOrderAction = { type: 'StartNewOrder'; payload: { order: OrderEntry } };
type AddItemAction = { type: 'AddItem'; payload: { orderItem: OrderItem } };
type UpdateItemAction = { type: 'UpdateItem'; payload: { orderItem: OrderItem } };
type DeleteItemAction = { type: 'DeleteItem'; payload: { sfaOrderItemId: number } };
type SelectItemAction = { type: 'SelectItem'; payload: { sfaOrderItemId: number } };
type SaveNotesAction = { type: 'SaveNotes'; payload: { notes: SFAOrderNote[] } };
type updateOrderFieldAction = { type: 'updateOrderField'; payload: { field: keyof OrderEntry; value: any } };

type Action =
  | SaveOrderAction
  | StartNewOrderAction
  | AddItemAction
  | DeleteItemAction
  | SelectItemAction
  | LoadOrderAction
  | LoadOrderAndSelectAction
  | SaveOrderHeaderAction
  | UpdateItemAction
  | SaveNotesAction
  | updateOrderFieldAction;

const OrderContext = createContext<OrderContextValue>({} as OrderContextValue);

const handlers: Record<string, (state: OrderEntry, action: Action) => OrderEntry> = {
  SaveOrder: (state: OrderEntry, action: SaveOrderAction): OrderEntry => action.payload.order,
  SaveOrderHeader: (state: OrderEntry, action: SaveOrderHeaderAction): OrderEntry => ({ ...state, ...action.payload.orderHeader }),
  StartNewOrder: (state: OrderEntry, action: SaveOrderAction): OrderEntry => action.payload.order,
  LoadOrder: (state: OrderEntry, action: LoadOrderAction): OrderEntry => action.payload.order,
  LoadOrderAndSelect: (state: OrderEntry, action: LoadOrderAndSelectAction): OrderEntry => {
    const { order, selectedOrderItemId } = action.payload;
    const sfaOrderItems = [...order.sfaOrderItems];
    sfaOrderItems.forEach((i) => (i.selected = i.sfaOrderItemId == selectedOrderItemId));
    return { ...order, sfaOrderItems: sfaOrderItems };
  },
  AddItem: (state: OrderEntry, action: AddItemAction): OrderEntry => {
    const { orderItem } = action.payload;
    const order = { ...state, sfaOrderItems: [...state.sfaOrderItems, orderItem] };
    order.total += orderItem.extendedPrice;
    return order;
  },
  UpdateItem: (state: OrderEntry, action: UpdateItemAction): OrderEntry => {
    const { orderItem } = action.payload;
    const sfaOrderItems = [...state.sfaOrderItems];
    const updatedItem = sfaOrderItems.find((oi) => oi.sfaOrderItemId == orderItem.sfaOrderItemId || oi.itemNumber == orderItem.itemNumber);
    if (!updatedItem) {
      console.error('UpdateItem: Item not found in order', orderItem);
      return state;
    }
    updatedItem.quantity = orderItem.quantity;
    return { ...state, sfaOrderItems: sfaOrderItems };
  },
  SelectItem: (state: OrderEntry, action: SelectItemAction): OrderEntry => {
    const { sfaOrderItemId } = action.payload;
    const sfaOrderItems = state.sfaOrderItems.map((item) => {
      const shouldBeSelected = item.sfaOrderItemId === sfaOrderItemId;
      return item.selected === shouldBeSelected ? item : { ...item, selected: shouldBeSelected };
    });
    return { ...state, sfaOrderItems };
  },
  DeleteItem: (state: OrderEntry, action: DeleteItemAction): OrderEntry => {
    const { sfaOrderItemId } = action.payload;
    const deletedItem = state.sfaOrderItems.find((oi) => oi.sfaOrderItemId == sfaOrderItemId);
    if (!deletedItem) {
      console.error('DeleteItem: Item not found in order', sfaOrderItemId);
      return state;
    }
    const order = { ...state, sfaOrderItems: [...state.sfaOrderItems.filter((oi) => oi.sfaOrderItemId != sfaOrderItemId)] };
    order.total -= deletedItem.extendedPrice;
    return order;
  },
  SaveNotes: (state: OrderEntry, action: SaveNotesAction): OrderEntry => ({ ...state, sfaOrderNotes: action.payload.notes }),
  updateOrderField: (state: OrderEntry, action: updateOrderFieldAction): OrderEntry => ({ ...state, [action.payload.field]: action.payload.value }),
};

const reducer = (state: OrderEntry, action: Action): OrderEntry => (handlers[action.type] ? handlers[action.type](state, action) : state);

export const OrderProvider: FC<ContextProviderProps> = (props) => {
  const { children } = props;
  const [order, dispatch] = useReducer(reducer, initialOrder);
  const [orderHeaderErrors, setOrderHeaderErrors] = useState<FieldErrors>({});
  const [warehousesFiltered, setWarehousesFiltered] = useState<Warehouse[]>([]);

  //Get greatest lead time of unavailable items. Null if no unavailable items. 21
  const [greatestLeadTime, itemNumberWithGreatestLeadTime] = useMemo(() => {
    if (!order || !order?.sfaOrderItems || order.sfaOrderItems.length < 1 || !order?.warehouseCode || !order?.orderType) {
      return [null, null];
    }
    return getGreatestLeadTimeOfUnavailableItems(order.sfaOrderItems, order.warehouseCode, order.orderType, order.orderQuote ?? '', order.isOpenOrderEdit ?? false, order.submitDate, order.sourcepros);
  }, [order?.sfaOrderItems, order?.warehouseCode, order?.orderType, order?.orderQuote, order?.isOpenOrderEdit]);


  const loadOrder = useCallback(
    async (sfaOrderId: number, selectedOrderItemId?: number, defaultShipToNumber?: string, defaultWarehouseCode?: string): Promise<OrderEntry | null> => {
      setOrderHeaderErrors({});
      try {
        const data = await orderApi.getSfaOrder(sfaOrderId, defaultShipToNumber, defaultWarehouseCode);

        console.log('LoadOrder Order:', data);

        if (selectedOrderItemId) {
          dispatch({ type: 'LoadOrderAndSelect', payload: { order: data, selectedOrderItemId } });
        } else {
          dispatch({ type: 'LoadOrder', payload: { order: data } });
        }
        return data;
      } catch (err) {
        console.error('Error loading order', err);
        return null;
      }
    },
    []
  );

  const saveOrder = useCallback((updatedOrder: OrderEntry): void => {
    orderApi.saveOrder(updatedOrder).then((data) => {
      dispatch({ type: 'SaveOrder', payload: { order: data } });
    });
  }, []);

  const saveOrderHeader = useCallback(async (): Promise<boolean> => {
    try {
      // The DTO is now built from the single source of truth: 'order'
      const data = await orderApi.saveOrderHeader(order);
      if (data === true) {
        // The dispatch is now less critical but can remain for consistency
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving order header', error);
      return false;
    }
  }, [order]);

  const addItem = useCallback((request: AddItemRequest): void => {
    orderApi.addItem(request, request.sfaOrderId).then((data) => {
      if (request.programId) {
        loadOrder(request.sfaOrderId);
      } else {
        dispatch({ type: 'AddItem', payload: { orderItem: data } });
      }
    });
  }, []);

  const updateItem = useCallback((sfaOrderItemId: number, quantity: number, programId?: number): void => {
    orderApi.updateItem(sfaOrderItemId, quantity).then((data) => {
      if (programId) {
        loadOrder(data.sfaOrderId);
      } else {
        dispatch({ type: 'UpdateItem', payload: { orderItem: data } });
      }
    });
  }, []);

  const selectItem = useCallback((sfaOrderItemId: number): void => {
    dispatch({ type: 'SelectItem', payload: { sfaOrderItemId } });
  }, []);

  const deleteItem = useCallback(
    (sfaOrderItemId: number, programId?: number): void => {
      orderApi.deleteItem(sfaOrderItemId, programId).then((data) => {
        if (programId) {
          loadOrder(order.sfaOrderId);
        } else {
          dispatch({ type: 'DeleteItem', payload: { sfaOrderItemId: data } });
        }
      });
    },
    [order.sfaOrderId]
  );

  const startNewOrder = useCallback(async (customerNumber: number, orderType: number, termCode: string, programId?: number): Promise<number> => {
    setOrderHeaderErrors({});

    //flush current state when starting new order
    dispatch({ type: 'StartNewOrder', payload: { order: initialOrder } });

    const apiCall = orderType === 0 ? orderApi.startNewWarehouseOrder : orderType === 1 ? orderApi.startNewDirectOrder : orderApi.startNewPlantOrder;
    try {
      const data = orderType === 3 ? await orderApi.startNewPromotionalOrder(customerNumber, termCode, programId) : await apiCall(customerNumber, termCode);
      dispatch({ type: 'StartNewOrder', payload: { order: data } });
      return data.sfaOrderId;
    } catch (err) {
      console.error('Error starting new order', err);
      throw err;
    }
  }, []);

  const saveNotes = useCallback((notes: SFAOrderNote[]): Promise<void> => {
    return orderApi.saveNotes(notes).then((response) => {
      dispatch({ type: 'SaveNotes', payload: { notes: response } });
    });
  }, []);

  const validateOrderHeader = useCallback(
    (poRequired: boolean): boolean => {
      const errors: FieldErrors = {};
      if (!order.customerPO && poRequired) {
        errors.customerPO = { rule: 'required', message: 'Customer PO is required.', severity: 'error' };
      }
      if (order.customerPO && order.customerPO.length > 44) {
        errors.customerPO = { rule: 'maxLength', message: 'Customer PO cannot exceed 44 characters.', severity: 'error' };
      }
      if (!order.shipToNumber) {
        errors.shipToNumber = { rule: 'format', message: 'Ship-to number is invalid.', severity: 'error' };
      }
      if (!order.requiredShipDate) {
        errors.requiredShipDate = { rule: 'required', message: 'Required Ship Date is required.', severity: 'error' };
      }

      if (order.requiredShipDate && greatestLeadTime !== null && greatestLeadTime > 0) {
        //Lead time of unavailable items validation
        const earliestShipDate = moment().add(greatestLeadTime, 'days').startOf('day');
        const requiredDate = moment(order.requiredShipDate).startOf('day');

        //The required date is earlier than the earliest ship date based on lead times
        if (requiredDate.isBefore(earliestShipDate)) {
          let msg = `The earliest ship date based on item lead times is ${earliestShipDate.format('ddd MMM DD YYYY')}.`;
          errors.greatestLeadTime = {
            rule: 'leadTime',
            message: msg,
            severity: 'warning',
          };
        }
      }

      if (!order.programId) {
        if (!order.orderQuote) {
          errors.orderQuote = { rule: 'required', message: 'Order Quote is required.', severity: 'error' };
        }
        if (order.isCustomAddress && order.customShipToAddress) {
          const { address1, city, state: addressState, zip } = order.customShipToAddress;
          if (!address1) errors.customAddress1 = { rule: 'required', message: 'Enter a valid address', severity: 'error' };
          if (!city) errors.customCity = { rule: 'required', message: 'Enter a valid city', severity: 'error' };
          if (!addressState) errors.customState = { rule: 'required', message: 'Enter a valid state', severity: 'error' };
          if (!zip) errors.customZip = { rule: 'required', message: 'Enter a valid zip', severity: 'error' };
        }
      }
      setOrderHeaderErrors(errors);
      return Object.values(errors).every((v) => !v);
    },
    [order.customerPO, order.shipToNumber, order.requiredShipDate, order.programId, order.orderQuote, order.isCustomAddress, order.customShipToAddress, setOrderHeaderErrors, greatestLeadTime]
  );

  const updateOrderField = useCallback((field: keyof OrderEntry, value: any): void => {
    dispatch({
      type: 'updateOrderField',
      payload: { field, value },
    });
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      order,
      loadOrder,
      saveOrder,
      startNewOrder,
      addItem,
      updateItem,
      selectItem,
      deleteItem,
      saveOrderHeader,
      saveNotes,
      orderHeaderErrors,
      setOrderHeaderErrors,
      validateOrderHeader,
      warehousesFiltered,
      setWarehousesFiltered,
      updateOrderField,
      greatestLeadTime,
      itemNumberWithGreatestLeadTime,
    }),
    [
      order,
      loadOrder,
      saveOrder,
      startNewOrder,
      addItem,
      updateItem,
      selectItem,
      deleteItem,
      saveOrderHeader,
      saveNotes,
      orderHeaderErrors,
      setOrderHeaderErrors,
      validateOrderHeader,
      warehousesFiltered,
      setWarehousesFiltered,
      updateOrderField,
      greatestLeadTime,
      itemNumberWithGreatestLeadTime,
    ]
  );

  return <OrderContext.Provider value={contextValue}>{children}</OrderContext.Provider>;
};

export default OrderContext;
